/**
 * CertificateSubjectsControl — iter-217c-v3
 *
 * Manages certificate subjects per category (merchant/customer/company/employee)
 * PLUS the CloudGM company display name (AR + EN) shown on every certificate.
 *
 * Each subject stores:
 *   • subject_ar / subject_en   — the dropdown label
 *   • narrative_ar / narrative_en — the body-text TEMPLATE that pre-fills the
 *     certificate PDF when this subject is picked. The admin can leave the
 *     template as-is, tweak it, or write their own.
 *
 * A ⇄ Translate button next to each pair calls `/api/utils/translate` so
 * the admin only writes one language and lets Gemini fill the other.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Trash2, Edit3, Check, X, Loader2, Store, Users, Building2,
  Briefcase, Save, Languages, ArrowRightLeft, ChevronDown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'merchant', ar: 'منصة التجار',  en: 'Merchants',  icon: Store,     color: '#8B5CF6' },
  { id: 'customer', ar: 'منصة العملاء', en: 'Customers',  icon: Users,     color: '#1E9BD9' },
  { id: 'company',  ar: 'للشركة',        en: 'Company',    icon: Building2, color: '#F59E0B' },
  { id: 'employee', ar: 'للموظفين',     en: 'Employees',  icon: Briefcase,  color: '#10b981' },
];

const BLANK_SUBJECT = {
  subject_ar:   '',
  subject_en:   '',
  narrative_ar: '',
  narrative_en: '',
};


// iter-217c-v3 — Reusable bilingual field pair with AR ⇄ EN translate buttons.
// Defined at module scope (NOT inside the component) so React keeps the input
// mounted between renders → typing focus is preserved.
function FieldPair({ label, form, setForm, prefix, which, multiline = false, translating, onTranslate, t }) {
  const arKey = `${prefix}_ar`, enKey = `${prefix}_en`;
  const busyAr2En = translating[`${which}-${prefix}-ar2en`];
  const busyEn2Ar = translating[`${which}-${prefix}-en2ar`];
  const baseCls = 'w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500';

  const commonProps = (key, dir, testid) => ({
    value: form[key] || '',
    onChange: (e) => setForm(f => ({ ...f, [key]: e.target.value })),
    dir,
    placeholder: dir === 'rtl' ? t('بالعربية', 'Arabic') : 'English',
    className: baseCls,
    'data-testid': testid,
  });

  return (
    <div className="space-y-2">
      <label className="text-[11px] text-white/60 font-semibold">{label}</label>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center">
        {multiline
          ? <textarea rows={4} {...commonProps(arKey, 'rtl', `${which}-${prefix}-ar`)} />
          : <input     {...commonProps(arKey, 'rtl', `${which}-${prefix}-ar`)} />}
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => onTranslate(which, 'ar2en', prefix)}
            disabled={busyAr2En || !form[arKey]?.trim()}
            title={t('ترجم من العربية إلى الإنجليزية', 'Translate AR → EN')}
            className="w-8 h-8 rounded-md bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 disabled:opacity-40 flex items-center justify-center"
            data-testid={`${which}-${prefix}-translate-ar2en`}
          >
            {busyAr2En ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-mono">AR→EN</span>}
          </button>
          <button
            type="button"
            onClick={() => onTranslate(which, 'en2ar', prefix)}
            disabled={busyEn2Ar || !form[enKey]?.trim()}
            title={t('ترجم من الإنجليزية إلى العربية', 'Translate EN → AR')}
            className="w-8 h-8 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40 flex items-center justify-center"
            data-testid={`${which}-${prefix}-translate-en2ar`}
          >
            {busyEn2Ar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-mono">EN→AR</span>}
          </button>
        </div>
        {multiline
          ? <textarea rows={4} {...commonProps(enKey, 'ltr', `${which}-${prefix}-en`)} />
          : <input     {...commonProps(enKey, 'ltr', `${which}-${prefix}-en`)} />}
      </div>
    </div>
  );
}

export default function CertificateSubjectsControl() {
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const authHeaders = useCallback(() => getAuthHeaders(), [getAuthHeaders]);

  const [activeCat, setActiveCat]     = useState('merchant');
  const [subjects, setSubjects]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [newForm, setNewForm]         = useState(BLANK_SUBJECT);
  const [openAdd, setOpenAdd]         = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [editForm, setEditForm]       = useState(BLANK_SUBJECT);
  const [translating, setTranslating] = useState({});   // {[key]: bool}

  // iter-218r (Feb 2026) — Company-name state removed. That field now lives
  // on its own dedicated page under "ختم ومسمى الشركة" so admins can find it
  // without hunting through the "company" tab of this screen.

  // ── Data ────────────────────────────────────────────────────────
  const loadSubjects = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/certificates/subjects?category=${activeCat}`,
        { headers: authHeaders() },
      );
      setSubjects(r.data.data || []);
    } catch (e) {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [activeCat, authHeaders]);

  useEffect(() => { loadSubjects(); }, [loadSubjects]);
  // Reset the add form each time the admin switches category.
  useEffect(() => { setNewForm(BLANK_SUBJECT); setOpenAdd(false); setEditingId(null); }, [activeCat]);

  // ── Translation helper ──────────────────────────────────────────
  const translate = async (text, source, target) => {
    if (!text?.trim()) return '';
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/utils/translate`,
        { text: text.trim(), source, target },
        { headers: authHeaders() },
      );
      return r.data.translated || '';
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الترجمة', 'Translation failed'));
      return '';
    }
  };

  const handleTranslate = async (which, direction, fields) => {
    // which     : 'new' | 'edit'
    // direction : 'ar2en' | 'en2ar'
    // fields    : 'subject' | 'narrative'
    const key = `${which}-${fields}-${direction}`;
    const [sLang, tLang] = direction === 'ar2en' ? ['ar', 'en'] : ['en', 'ar'];
    setTranslating(m => ({ ...m, [key]: true }));
    try {
      const form  = which === 'new' ? newForm : editForm;
      const setter = which === 'new' ? setNewForm : setEditForm;
      const srcKey = `${fields}_${sLang}`;
      const tgtKey = `${fields}_${tLang}`;
      const out = await translate(form[srcKey], sLang, tLang);
      if (out) setter(f => ({ ...f, [tgtKey]: out }));
    } finally {
      setTranslating(m => ({ ...m, [key]: false }));
    }
  };

  // ── Subject CRUD ────────────────────────────────────────────────
  const addSubject = async () => {
    if (!newForm.subject_ar.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/certificates/subjects`, {
        category:     activeCat,
        subject_ar:   newForm.subject_ar.trim(),
        subject_en:   newForm.subject_en.trim(),
        narrative_ar: newForm.narrative_ar.trim(),
        narrative_en: newForm.narrative_en.trim(),
        is_active:    true,
      }, { headers: authHeaders() });
      toast.success(t('✓ أُضيف الموضوع', '✓ Subject added'));
      setNewForm(BLANK_SUBJECT);
      setOpenAdd(false);
      loadSubjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الإضافة', 'Add failed'));
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      subject_ar:   row.subject_ar   || '',
      subject_en:   row.subject_en   || '',
      narrative_ar: row.narrative_ar || '',
      narrative_en: row.narrative_en || '',
    });
  };

  const saveEdit = async () => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/certificates/subjects/${editingId}`,
        editForm,
        { headers: authHeaders() },
      );
      toast.success(t('✓ تم التحديث', '✓ Updated'));
      setEditingId(null);
      loadSubjects();
    } catch (e) {
      toast.error(t('فشل التحديث', 'Update failed'));
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/certificates/subjects/${row.id}`,
        { is_active: !row.is_active }, { headers: authHeaders() });
      loadSubjects();
    } catch (e) {
      toast.error(t('فشل', 'Failed'));
    }
  };

  const deleteSubject = async (id) => {
    if (!window.confirm(t('هل أنت متأكد من الحذف؟', 'Delete this subject?'))) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/certificates/subjects/${id}`, { headers: authHeaders() });
      loadSubjects();
      toast.success(t('تم الحذف', 'Deleted'));
    } catch (e) {
      toast.error(t('فشل الحذف', 'Delete failed'));
    }
  };

  // iter-218r (Feb 2026) — `saveCompany` and the AR/EN company-name form
  // moved to /control-panel-settings/company-mgmt/branding/name so the field
  // is easy to find. It is no longer rendered on this screen.

  // ── Small reusable AR ⇄ EN field pair — DEFINED AT MODULE SCOPE
  //    (see the top of the file). We just wrap the caller with the required
  //    props so the child stays remount-free.
  const renderFieldPair = (label, form, setForm, prefix, which, multiline = false) => (
    <FieldPair
      label={label}
      form={form}
      setForm={setForm}
      prefix={prefix}
      which={which}
      multiline={multiline}
      translating={translating}
      onTranslate={handleTranslate}
      t={t}
    />
  );

  // ── Render ──────────────────────────────────────────────────────
  return (
    <DashboardLayout
      title="التحكم بإدارة الشركة"
      titleEn="Company Management Control"
      subtitle="التحكم بمواضيع الشهادات لكل فئة + اسم الشركة الظاهر في الشهادات"
      subtitleEn="Manage certificate subjects per category + the company name shown on all certificates"
      screenCode="FZLC-CTRL-MGMT"
      backPath="/control-panel-settings/company-mgmt"
      onBack={() => navigate('/control-panel-settings/company-mgmt')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'CLOUDGM FZ LLC (للتحكم)', labelEn: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control', path: '/control-panel-settings/company-mgmt' },
        { labelAr: 'الشهادات', labelEn: 'Certificates' },
      ]}
    >
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" dir={isAr ? 'rtl' : 'ltr'} data-testid="cert-subjects-control">
        {/* Category tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="cat-tabs">
          {CATEGORIES.map(c => {
            const Icon = c.icon;
            const active = activeCat === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`h-14 rounded-xl border transition text-sm font-medium flex items-center justify-center gap-2 ${
                  active ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
                style={active
                  ? { background: `${c.color}20`, borderColor: `${c.color}80` }
                  : { background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.1)' }}
                data-testid={`cat-tab-${c.id}`}
              >
                <Icon className="w-4 h-4" style={{ color: active ? c.color : undefined }} />
                {isAr ? c.ar : c.en}
              </button>
            );
          })}
        </div>

        {/* iter-218r (Feb 2026) — Company-name control removed from this page.
             It now lives on its own dedicated screen accessible via
             "ختم ومسمى الشركة" → "مسمى الشركة" so admins can find it easily. */}

        {/* Subjects manager */}
        <section className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-white">
              {t(`قائمة مواضيع الشهادات — ${CATEGORIES.find(c => c.id === activeCat)?.ar}`,
                 `Certificate Subjects — ${CATEGORIES.find(c => c.id === activeCat)?.en}`)}
            </h3>
            <Button
              onClick={() => setOpenAdd(v => !v)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
              data-testid="open-add-btn"
            >
              <Plus className="w-4 h-4 ml-1" /> {openAdd ? t('إغلاق', 'Close') : t('موضوع جديد', 'New subject')}
            </Button>
          </div>

          {/* Add form (expandable) */}
          <AnimatePresence>
            {openAdd && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden mb-4"
              >
                <div className="p-4 rounded-xl bg-[#050505] border border-white/10 space-y-4" data-testid="new-subject-form">
                  {renderFieldPair(
                    t('الموضوع (عربي / إنجليزي) *', 'Subject (Arabic / English) *'),
                    newForm, setNewForm, 'subject', 'new',
                  )}
                  {renderFieldPair(
                    t('السرد داخل الشهادة (يُملأ تلقائياً عند اختيار الموضوع)',
                      'Certificate narrative (auto-fills when the subject is picked)'),
                    newForm, setNewForm, 'narrative', 'new', true,
                  )}
                  <div className="flex justify-end">
                    <Button
                      onClick={addSubject}
                      disabled={!newForm.subject_ar.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="save-new-btn"
                    >
                      <Plus className="w-4 h-4 ml-1" /> {t('إضافة الموضوع', 'Add subject')}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* List */}
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
          ) : subjects.length === 0 ? (
            <div className="p-8 text-center text-white/50 border border-dashed border-white/10 rounded-xl">
              {t('لا توجد مواضيع محفوظة لهذه الفئة.', 'No subjects saved for this category yet.')}
            </div>
          ) : (
            <div className="space-y-2" data-testid="subjects-list">
              {subjects.map((row) => {
                const isEditing = editingId === row.id;
                return (
                  <div key={row.id} className={`rounded-xl border ${row.is_active === false ? 'opacity-50' : ''} ${isEditing ? 'border-blue-500/50 bg-blue-500/[0.03]' : 'border-white/10 bg-black/20'}`}>
                    {isEditing ? (
                      <div className="p-4 space-y-4">
                        {renderFieldPair(t('الموضوع', 'Subject'), editForm, setEditForm, 'subject', 'edit')}
                        {renderFieldPair(t('السرد', 'Narrative'), editForm, setEditForm, 'narrative', 'edit', true)}
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setEditingId(null)} className="border-white/10 text-white/70 h-9">
                            <X className="w-4 h-4 ml-1" /> {t('إلغاء', 'Cancel')}
                          </Button>
                          <Button onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9">
                            <Check className="w-4 h-4 ml-1" /> {t('حفظ', 'Save')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-sm text-white font-semibold">{row.subject_ar || '—'}</span>
                            <span className="text-[11px] text-white/40">·</span>
                            <span className="text-sm text-white/70" dir="ltr">{row.subject_en || '—'}</span>
                            {(row.narrative_ar || row.narrative_en) && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
                                {t('يحتوي سرداً', 'Has narrative')}
                              </span>
                            )}
                          </div>
                          {(row.narrative_ar || row.narrative_en) && (
                            <div className="text-[11px] text-white/50 line-clamp-2 italic">
                              {isAr ? (row.narrative_ar || row.narrative_en) : (row.narrative_en || row.narrative_ar)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => toggleActive(row)}
                          className={`text-[10px] px-2 py-1 rounded-full font-medium flex-shrink-0 ${
                            row.is_active !== false
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                              : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                          }`}
                          data-testid={`subject-toggle-${row.id}`}
                        >
                          {row.is_active !== false ? t('نشط', 'Active') : t('معطّل', 'Inactive')}
                        </button>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(row)}
                                className="text-blue-400 hover:bg-blue-500/10 h-8 w-8 p-0 flex-shrink-0">
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteSubject(row.id)}
                                className="text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0 flex-shrink-0"
                                data-testid={`subject-delete-${row.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </DashboardLayout>
  );
}
